import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FulfillService } from '@app/pages/workspace/fulfill/fulfill.service';
import { OrderService } from '@app/services/orders/order.service';
import { ShopService } from '@app/services/shops/shop.service';
import { BoxType, Order } from '@pdqdev/pdq-shared/dist/entities';
import { IBoxSettingsData } from '@pdqdev/pdq-shared/dist/interfaces';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TimeUnits, getSignOfCurrency } from 'src/utils';
import { getOrderTotalWeight } from '../../../../../../../../utils/order.utils';

const ratesCache: Map<string, any> = new Map();
interface ShipmentRateObj {
  carrierServiceLevelId: any;
  price: string;
  currency: string;
  boxId: number;
  multiLabelSelectedOrderBoxes?: any;
}
enum ResultMessage {
  UNKNOWN = `Unable to preview rates`,
  NO_VALID_RATES_FOR_ORDER = `No valid rates were found for the order`,
  NO_RATES = `No shipping rates available`,
  NO_VALIES_RATES_FOR_SERVICE_LEVEL = `No valid rates were found for same provider service level`,
  UPDATE_ERROR = 'Failed to update order details',
  UPDATE_SUCCESS = 'Order details updated',
}
enum PackageType {
  TEMPLATE = 0,
  CUSTOM = 1,
}
@Component({
  selector: 'switch-order-package',
  templateUrl: './switch-order-package.component.html',
  styleUrls: ['switch-order-package.component.scss'],
  standalone: false,
})
export class SwitchOrderPackageComponent implements OnInit, OnChanges, OnDestroy {
  loadingRates;
  loadingConfirm;
  packageForm: UntypedFormGroup;
  shopBoxes;
  initialBox;
  initialShippingWeight;
  isMultiParcel;
  rateObj: ShipmentRateObj;
  rateStr = '';
  changesLastSnapshot;
  currentBatch;
  packageType: PackageType;
  batchSubscription: Subscription;
  shopMetadata;
  destroy$ = new Subject();
  constructor(
    private fulfillService: FulfillService,
    private shopService: ShopService,
    private orderService: OrderService,
    @Inject(MAT_DIALOG_DATA) public data: { order: Order; orderItem: any },
    public dialogRef: MatDialogRef<SwitchOrderPackageComponent>,
    private toastService: ToastService,
  ) {}
  ngOnDestroy(): void {
    if (this.batchSubscription) {
      this.batchSubscription.unsubscribe();
    }
    this.destroy$.next(true);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.setIsMultiParcel();
      this.setInitialBox();
      this.setInitialWeight();
    }
  }

  ngOnInit(): void {
    this.initShopMetadata();
    this.currentBatch = this.fulfillService.getActiveBatch;
    this.setIsMultiParcel();
    this.initForm();
    this.initShopBoxes();
    this.setInitialBox();
    this.setInitialWeight();
    this.initPrepareBatchListener();
  }

  async previewRates() {
    if (this.validateForm(this.packageType)) {
      // must use orderId for unique cache..
      const orderId = this.data.order?.id;
      const cacheKey = orderId + this.getCacheKey();
      const cachedValue = ratesCache.get(cacheKey);
      this.loadingRates = true;
      if (cachedValue) {
        this.rateObj = cachedValue.rateObj;
        this.changesLastSnapshot = cachedValue.changesLastSnapshot;
        this.setRateStr();
      } else {
        try {
          const previewPayload: any = this.preparePreviewConfig();
          this.rateObj = await this.getChangedBoxOrderRate(previewPayload);
          if (this.rateObj?.currency && this.rateObj?.price) {
            ratesCache.set(cacheKey, { rateObj: this.rateObj, changesLastSnapshot: this.changesLastSnapshot });
            this.setRateStr();
            this.packageForm.markAsPristine();
          }
        } catch (e) {
          console.log(e);
          this.rateStr = `${ResultMessage.UNKNOWN}`;
        }
      }

      this.loadingRates = false;
    }
  }

  onConfirm(): void {
    this.loadingConfirm = true;
    const referId: any = { orderId: this.data?.order?.id };
    const payload: any = this.changesLastSnapshot;
    if (!payload.box && !payload.boxId) {
      payload.box = this.initialBox;
    }
    payload.serviceLevelId = this.rateObj?.carrierServiceLevelId ?? null;
    payload.ratePrice = this.rateObj?.price ?? null;
    payload.rateCurrency = this.shopMetadata.currency ?? null;

    if (this.isMultiParcel) {
      payload.multiLabelBoxes = this.rateObj?.multiLabelSelectedOrderBoxes?.find(
        (val) => val.orderNumber === +this.data?.order?.orderNumber,
      );

      referId.orderItemId = this.data?.orderItem?.id;
    }

    this.orderService.switchOrderPackageAndCreateShipment(payload, referId).subscribe(
      (updatedOrder) => {
        this.initialBox = updatedOrder.box;
        this.fulfillService.setActiveOrder(updatedOrder);
        // this.showToast(ResultMessage.UPDATE_SUCCESS, 'success');
        // After updating the order details, should wait until batch succeed, handled by initPrepareBatchListener function //
      },
      (err) => {
        console.log(err);
        this.showToast(ResultMessage.UPDATE_ERROR, 'danger');
        this.loadingConfirm = false;
        this.onClose();
      },
    );
  }

  onClose(event?) {
    this.dialogRef.close(event);
  }

  initForm() {
    this.packageForm = new UntypedFormGroup({});
    this.packageForm.valueChanges.subscribe(() => {
      this.rateObj = null;
      this.rateStr = '';
    });
  }
  setInitialBox() {
    this.initialBox = this.data?.orderItem ? this.data.orderItem.box : this.data.order?.box;
  }
  initShopBoxes() {
    this.shopService.shopDefaultBoxes$.subscribe(
      ({ activeBoxes, inactiveBoxes }) => (this.shopBoxes = [...activeBoxes]),
    );
  }

  initMode() {
    this.isMultiParcel = this.data?.orderItem;
  }
  private setInitialWeight() {
    if (!this.isMultiParcel && this.data?.order) {
      this.initialShippingWeight = getOrderTotalWeight(this.data.order, this.shopMetadata);
    }
  }
  setPackageType(newType) {
    this.packageType = newType;
  }
  setIsMultiParcel() {
    this.isMultiParcel = this.fulfillService.isShopOnMultiParcelMode();
  }
  private setRateStr() {
    this.rateStr = `${this.rateObj.currency ?? ''} ${this.rateObj.price ?? ''}`;
  }
  private initShopMetadata() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.shopMetadata = shop?.metadata;
      if (this.shopMetadata) {
        this.setInitialWeight();
      }
    });
  }
  private validateForm(packageType: PackageType) {
    const weightForm = this.packageForm.controls.shippingWeight as UntypedFormGroup;
    const customBoxForm = this.packageForm.controls.customBox as UntypedFormGroup;
    const boxControl = this.packageForm.controls.boxId;

    const weightIsValid = weightForm.valid || weightForm.pristine;

    switch (packageType) {
      case PackageType.TEMPLATE:
        if (boxControl.invalid) {
          boxControl.markAsDirty();
        }
        return weightIsValid && boxControl.valid;
      case PackageType.CUSTOM:
        if (customBoxForm.invalid) {
          customBoxForm.controls.length.markAsDirty();
          customBoxForm.controls.height.markAsDirty();
          customBoxForm.controls.width.markAsDirty();
        }
        return weightIsValid && customBoxForm.valid;
    }
  }
  private getCacheKey() {
    const controls = this.packageForm.controls;
    const cacheKeyBox =
      this.packageType === PackageType.CUSTOM
        ? `CUSTOM BOX - ${controls.customBox.value.length}X${controls.customBox.value.width}X${controls.customBox.value.height} ${controls.customBox.value.distanceUnit}`
        : `TEMPLATE BOX - ${controls.boxId.value}`;
    const cacheKeyWeight = `SHIPPING WEIGHT - ${controls.shippingWeight.value.weightAmount} ${controls.shippingWeight.value.weightUnit}`;
    return `${cacheKeyBox} - ${cacheKeyWeight}`;
  }
  private async getChangedBoxOrderRate(boxId: number): Promise<any> {
    const { id, orderBatch, deliveryTransaction } = this.data?.order;
    const slToken = deliveryTransaction.serviceLevelToken;

    if (!id || !orderBatch || !deliveryTransaction || !boxId) {
      this.showToast(ResultMessage.UNKNOWN, 'danger');
      return;
    }

    const ratesOptions = await this.orderService.getOrdersRates(
      [id],
      orderBatch.id,
      boxId,
      undefined,
      undefined,
      this.preparePreviewConfig(),
    );

    if (ratesOptions?.notSupportedRatesCalculation) {
      this.rateStr = ResultMessage.NO_RATES;
      return;
    }

    if (!ratesOptions) {
      this.rateStr = ResultMessage.NO_RATES;
      return;
    }

    const currentOrderRates = ratesOptions?.rateInfo?.totalServiceLevelsRatesData?.ordersRatesOptions.find(
      (val) => val?.orderId === id,
    );
    if (!currentOrderRates) {
      this.showToast(ResultMessage.NO_VALID_RATES_FOR_ORDER, 'danger');
      return;
    }

    if (!currentOrderRates.rates?.length) {
      this.showToast(ResultMessage.NO_RATES, 'danger');
      return;
    }

    let finalRatesObj;

    if (slToken) {
      // if service level token exists, we must ensure the rate with same service level provider
      finalRatesObj = currentOrderRates.rates.find(
        (rate) =>
          rate?.carrierServiceLevelInternalToken === slToken || rate?.carrierServiceLevelId?.courier_id === slToken,
      );

      if (!finalRatesObj) {
        this.showToast(ResultMessage.NO_VALIES_RATES_FOR_SERVICE_LEVEL, 'danger');
        return;
      }
    } else {
      // if no support for compare service level tokens
      finalRatesObj = currentOrderRates.rates[0];
    }
    return {
      price: finalRatesObj.price?.toString(),
      multiLabelSelectedOrderBoxes:
        ratesOptions?.rateInfo?.totalServiceLevelsRatesData?.multiLabelSelectedOrderBoxes ?? null,
      carrierServiceLevelId: finalRatesObj?.carrierServiceLevelId ?? null,
      boxId: boxId,
      currency: getSignOfCurrency(ratesOptions?.rateInfo?.totalServiceLevelsRatesData?.senderCurrencyLocal),
    };
  }

  private showToast(message, mode: 'danger' | 'success' | 'info' | 'secondary') {
    this.toastService.show(PdqToastComponent, {
      mode: mode,
      message: message,
      duration: TimeUnits._SECOND_IN_MS * 3,
    });
  }
  private initPrepareBatchListener() {
    this.batchSubscription = this.fulfillService.onPrepareBatchFinish.subscribe((batch) => {
      if (batch.id == this.currentBatch.id) {
        let batchResentSuccess: boolean = false;
        if (batch.orders?.length == this.currentBatch.orders?.length) {
          batchResentSuccess = true;
        }
        this.loadingConfirm = false;
        this.loadingRates = false;
        this.dialogRef.close({ success: batchResentSuccess, updatedBox: this.initialBox });
      }
    });
  }

  private preparePreviewConfig() {
    const { id: orderId } = this.data?.order;
    const res = {
      changes: {
        orders: [],
      },
    };

    const packageChanges = this.getPackageChanges();
    this.changesLastSnapshot = packageChanges;
    if (this.data?.orderItem && this.isMultiParcel) {
      const orderChanges = {
        orderId,
        orderItems: [
          {
            orderItemId: this.data?.orderItem.id,
            ...packageChanges,
          },
        ],
      };
      res.changes.orders.push(orderChanges);
    } else {
      const orderChanges = {
        orderId,
        ...packageChanges,
      };
      res.changes.orders.push(orderChanges);
    }
    return res;
  }
  private getPackageChanges() {
    let box;
    const formControls = this.packageForm.controls;
    const weight = formControls.shippingWeight.dirty ? formControls.shippingWeight.value : undefined;
    const customBox =
      formControls.customBox.dirty && this.packageType === PackageType.CUSTOM ? this.getCustomBoxDto() : undefined;
    const boxId =
      formControls.boxId.dirty && this.packageType === PackageType.TEMPLATE ? formControls.boxId.value : undefined;

    if (boxId) {
      box = this.shopBoxes.find((box) => box.id === boxId);
    }
    if (!this.isMultiParcel) {
      box = box ?? customBox;
    }

    return { weight, box };
  }

  private getCustomBoxDto() {
    const existingCustomBoxId = this.data.order?.box?.isCustom ? this.data.order?.box?.id : undefined;
    const boxDto: IBoxSettingsData = {
      ...this.packageForm.value.customBox,
      name: 'Custom box',
      priority: 0,
      type: BoxType.BOX,
      isActive: true,
      isCustom: true,
      id: existingCustomBoxId,
    };
    return boxDto;
  }
}
